<script>
import axios from 'axios';
import { cloneDeep } from 'lodash';
import RbxRemoteCombobox from '@/shared/reybex/RbxRemoteCombobox';
import RbxRemoteComboboxMultiple from '@/shared/reybex/RbxRemoteComboboxMultiple';
import RbxConfirmDialog from '@/shared/reybex/RbxConfirmDialog';
import validatorMixin from '@/mixins/validator';
import snackbarMixin from '@/mixins/snackbar';

export default {
  name: 'MeasureSelectionDialog',
  components: {
    RbxRemoteCombobox,
    RbxRemoteComboboxMultiple,
    RbxConfirmDialog,
  },
  mixins: [snackbarMixin, validatorMixin],
  props: {
    measure: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      visible: false,
      priorityOptions: [{ id: 3, text: 'Sehr hoch' }, { id: 2, text: 'Hoch' }, { id: 1, text: 'Normal' }, { id: 0, text: 'Niedrig' }],
      measureOption: {},
      measureFormData: {},
      isEditable: false,
      isSaving: false,
      isConfirmDialogDisplayed: false,
      confirmDeletionMessage: 'Soll die Maßnahme gelöscht werden?',
    };
  },
  watch: {
    measureOption() {
      if (this.measureOption && this.measureOption.id) {
        axios.get(`/measure/${this.measureOption.id}`)
          .then((response) => {
            this.measureFormData = response.data;
            this.isEditable = !this.measureFormData || this.measureFormData.tenantId;
          });
      } else {
        this.measureFormData = {};
        this.isEditable = true;
      }
    },
  },
  created() {
    console.log('created: start..');
    this.isEditable = !this.measure || this.measure.tenantId;
    this.measureOption = cloneDeep(this.measure);

    if (this.measure) {
      this.measureOption.translatedName = this.measure.name;
    }
    console.log('created: DONE.');
  },
  mounted() {
    this.visible = true;
  },
  beforeDestroy() {
    this.visible = false;
  },
  methods: {
    submit() {
      this.$emit('submit', this.measureFormData);
    },
    close() {
      this.$emit('cancel');
    },
    onDeselectMeasure() {
      this.measureOption = {};
    },
    onSave() {
      if (!this.measureFormData.name) {
        this.showErrorMessage('Bitte geben Sie eine Bezeichnung für die Maßnahme ein.');
      }
      if (!this.measureFormData.priority) {
        this.showErrorMessage('Bitte wählen Sie eine Priorität für die Maßnahme aus.');
      }
      if (!this.measureFormData.description) {
        this.showErrorMessage('Bitte geben Sie eine Beschreibung für die Maßnahme ein.');
      }
      this.isSaving = true;
      axios.post('/measure/', this.measureFormData)
        .then((response) => {
          if (this.measureFormData.id) {
            this.showSuccessMessage('Die Maßnahme wurde aktualisiert.');
          } else {
            this.showSuccessMessage('Die Maßnahme wurde erstellt.');
          }
          this.measureFormData = response.data;
          this.measureOption = response.data;

          if (response.data && response.data.name) {
            this.measureOption.translatedName = response.data.name;
          }
          this.isEditable = !this.measureFormData || this.measureFormData.tenantId;
        })
        .catch(() => {
          this.showErrorMessage('Ein technischer Fehler ist aufgetreten. Der Speichervorgang ist fehlgeschlagen.');
        }).finally(() => {
          this.isSaving = false;
        });
    },
    onDelete() {
      this.isConfirmDialogDisplayed = true;
    },
    onRefuse() {
      this.isConfirmDialogDisplayed = false;
    },
    onConfirm() {
      this.isConfirmDialogDisplayed = false;
      if (!(this.measureFormData && this.measureFormData.id)) {
        this.showErrorMessage('Ein technischer Fehler ist aufgetreten. Der Löschvorgang ist fehlgeschlagen.');
      }
      axios.delete(`/measure/${this.measureFormData.id}`)
        .then(() => {
          this.showSuccessMessage('Die Maßnahme wurde gelöscht.');

          this.measureFormData = {};
          this.measureOption = {};
          this.isEditable = true;
        })
        .catch(() => {
          this.showErrorMessage('Ein technischer Fehler ist aufgetreten. Der Löschvorgang ist fehlgeschlagen.');
        });
    },
  },
};
</script>

<template>
  <v-dialog v-model="visible"
            persistent
            max-width="1000px">

    <v-card>
      <v-card-title class="title pa-3">
        Maßnahmenauswahl
      </v-card-title>

      <v-card-text>
        <v-container grid-list-md class="pa-0">
          <v-layout row wrap>
            <!-- TODO: Increase the number of displayed characters. -->
            <v-flex xs12 md12 py-0>
              <h4>Maßnahme auswählen:</h4>
            </v-flex>
            <v-flex xs11 md11 py-0>
              <rbx-remote-combobox
                v-model="measureOption"
                label=""
                :selected="measureOption"
                domain="Measure"
              />
            </v-flex>

            <v-flex xs1 md1 py-0>
              <v-btn @click="onDeselectMeasure" icon slot="activator">
                <v-icon>clear</v-icon>
              </v-btn>
            </v-flex>

            <v-flex xs12 md12 py-0>
              &nbsp;
            </v-flex>

            <v-flex xs12 md12 py-0>
              <h4>Maßnahme bearbeiten (nur für eigene Maßnahmen möglich):</h4>
            </v-flex>

            <v-flex xs12 md12 py-0>
              &nbsp;
            </v-flex>

            <v-toolbar dark dense height="40" color="blue-grey lighten-2">
              <v-tooltip bottom>
                <v-btn
                  slot="activator"
                  :loading="isSaving"
                  :disabled="!isEditable"
                  icon
                  @click="onSave"
                >
                  <v-icon>save</v-icon>
                </v-btn>
                <span>Speichern</span>
              </v-tooltip>

              <v-tooltip bottom>
                <v-btn @click="onDelete" icon slot="activator" :disabled="!isEditable">
                  <v-icon>delete</v-icon>
                </v-btn>
                <span>Löschen</span>
              </v-tooltip>
            </v-toolbar>

            <v-flex xs12 md12 py-0>
              <v-textarea
                v-model="measureFormData.name"
                rows="3"
                :readonly="!isEditable"
              />
            </v-flex>

            <v-flex xs4 md4 py-0>
              <v-select
                :items="priorityOptions"
                v-model="measureFormData.priority"
                item-value="id"
                item-text="text"
                label="Priorität"
                :readonly="!isEditable"
              />
            </v-flex>

            <v-flex xs12 md12 py-0>
              <v-textarea
                v-model="measureFormData.description"
                rows="9"
                :readonly="!isEditable"
              />
            </v-flex>

          </v-layout>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer/>
        <v-btn @click.native="close"
               color="red"
               flat>
          Schließen
        </v-btn>

        <v-btn @click.native="submit"
               color="blue"
               flat>
          Zuweisen
        </v-btn>

      </v-card-actions>
    </v-card>

    <rbx-confirm-dialog @confirm="onConfirm" @refuse="onRefuse"
                        :message="confirmDeletionMessage"
                        :is-displayed="isConfirmDialogDisplayed" />

  </v-dialog>
</template>
